import { ChakraBox, ChakraFlex, Text } from '@equip.health/ui';
import { CarePlanProblemResponse } from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalCardManifestationRow } from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalCardManifestationRow';
import { CarePlanGoalCardProblemRow } from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalCardProblemRow';
import { CarePlanInterventionRow } from '~/components/maud/patients/profile/tabs/careplan/CarePlanInterventionRow';
import { CarePlanDescriptionRow } from './CarePlanDescriptionRow';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

type Props = {
  problem: CarePlanProblemResponse;
  treatmentEpisodeId: string;
  displayProblemLabels?: boolean;
  onEditProblemClick: (problem: CarePlanProblemResponse) => void;
};

export const CarePlanTreatmentGoalExpandedView = ({
  problem,
  treatmentEpisodeId,
  displayProblemLabels = true,
  onEditProblemClick,
}: Props) => {
  const { carePlanM4CustomInterventions } = useFlags<LDFlags>();

  const goal = problem.goals?.[0];

  const sortedManifestations = useMemo(
    () =>
      problem.manifestations.toSorted(
        (m1, m2) =>
          m1.pathwayManifestation.sortOrder - m2.pathwayManifestation.sortOrder,
      ),
    [problem.manifestations],
  );

  const interventionsToDisplay = useMemo(() => {
    if (!goal) return [];
    const interventions = carePlanM4CustomInterventions
      ? goal.interventions
      : goal.interventions.filter((intervention) => !intervention.isCustom);

    return interventions.toSorted((i1, i2) => {
      const phaseStage1 = i1.treatmentPhaseStage?.sortOrder ?? Infinity;
      const phaseStage2 = i2.treatmentPhaseStage?.sortOrder ?? Infinity;
      if (phaseStage1 !== phaseStage2) {
        return phaseStage1 - phaseStage2;
      }
      return (
        (i1.intervention?.sortOrder ?? Infinity) -
        (i2.intervention?.sortOrder ?? Infinity)
      );
    });
  }, [goal, carePlanM4CustomInterventions]);

  const isDescriptionPresent = Boolean(
    goal?.descriptionDetails?.description?.trim(),
  );

  if (!goal) {
    return null;
  }

  return (
    <ChakraBox pb="18px">
      <ChakraBox>
        {isDescriptionPresent && (
          <CarePlanDescriptionRow
            descriptionDetails={goal.descriptionDetails}
            goalId={goal.id}
          />
        )}
      </ChakraBox>

      <ChakraBox marginTop="18px">
        <Text color="secondary.70" marginBottom="12px" variant="captionMedium">
          Problem details
        </Text>
        {sortedManifestations.length > 0 && (
          <ChakraFlex direction="column" gap="18px">
            {sortedManifestations.map((manifestation) => (
              <CarePlanGoalCardManifestationRow
                key={`${problem.id}-${manifestation.pathwayManifestation.code}`}
                manifestation={manifestation}
                onEditProblemClick={() => onEditProblemClick(problem)}
                problem={problem}
              />
            ))}
          </ChakraFlex>
        )}
        {sortedManifestations.length === 0 && (
          <CarePlanGoalCardProblemRow
            displayProblemLabels={displayProblemLabels}
            onEditProblemClick={onEditProblemClick}
            problem={problem}
          />
        )}
      </ChakraBox>

      {interventionsToDisplay.length > 0 && (
        <ChakraBox marginTop="18px">
          <Text
            color="secondary.70"
            marginBottom="12px"
            variant="captionMedium"
          >
            Related interventions
          </Text>
          <ChakraBox>
            {interventionsToDisplay.map((intervention) => (
              <CarePlanInterventionRow
                intervention={intervention}
                isForPhaseOrStageClassification={false}
                key={intervention.id}
                treatmentEpisodeId={treatmentEpisodeId}
              />
            ))}
          </ChakraBox>
        </ChakraBox>
      )}
    </ChakraBox>
  );
};
